import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

// Portion of a video slide that must be visible before it autoplays.
const VIDEO_VISIBILITY_THRESHOLD = 0.6;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#observeVideoSlides();
  }

  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #videoObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#videoObserver?.disconnect();
  }

  /**
   * Autoplays video slides once they're visible to the user and pauses them once they're not,
   * so product videos play back without requiring a tap/click, on both mobile and desktop.
   */
  #observeVideoSlides() {
    if (prefersReducedMotion()) return;

    const videoSlides = [...this.querySelectorAll('deferred-media')].filter((media) =>
      media.closest('.product-media-container--video, .product-media-container--external_video')
    );

    if (!videoSlides.length) return;

    this.#videoObserver = new IntersectionObserver(this.#handleVideoIntersection, {
      threshold: VIDEO_VISIBILITY_THRESHOLD,
    });

    for (const slide of videoSlides) {
      this.#videoObserver.observe(slide);
    }
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleVideoIntersection = (entries) => {
    for (const entry of entries) {
      const deferredMedia = /** @type {*} */ (entry.target);

      if (entry.isIntersecting) {
        deferredMedia.loadContent(false);
        deferredMedia.playMedia();
      } else {
        deferredMedia.pauseMedia();
      }
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
